import { chartColours } from "../../../themes/colours"

import {
  VISITOR_AND_SPEND_TYPES,
  VISITOR_AND_SPEND_TIME_SLOTS_DAY,
  VISITOR_AND_SPEND_TIME_SLOTS_NIGHT,
} from './atoms'

const getVisitorAndSpendColorScale = (id, dayOrNight) => {
  const typ = VISITOR_AND_SPEND_TYPES.find((typ) => typ.id === id)
  let dataScale = structuredClone(typ.dataScale)[dayOrNight]

  // Need a flat map in the form:
  // ['color', 'value', 'colour', ..., 'value', 'colour']
  // See MapLibre spec.
  dataScale = dataScale.map((item) => [item.value, item.fillColor])
  dataScale = dataScale.flat()

  // Remove first 'value'. See flat map format above.
  dataScale.splice(0, 1)
  
  return dataScale
}

const layerDefs = {
  layer: {
    id: "visitor-and-spend-visitor-hex-day",
    type: "fill",
    filter: [
      // MapLibre doesn't like negative numbers
      'all',
      ['has', 'visitor'],
      ['>', ['get', 'visitor'], 0],
      ['in', ['get', 'time_slot'], ['literal', VISITOR_AND_SPEND_TIME_SLOTS_DAY]],
    ],
    paint: {
      "fill-color": [
        "step",
        ["get", "visitor"],
        ...getVisitorAndSpendColorScale('visitor', 'day'),
      ],
      "fill-opacity": 0.7,
      "fill-outline-color": "#252C34",
    },
    layout: {
      visibility: "none",
    },
  },
  overLayers: [
    {
      id: "visitor-and-spend-visitor-hex-night",
      type: "fill",
      filter: [
        // MapLibre doesn't like negative numbers
        'all',
        ['has', 'visitor'],
        ['>', ['get', 'visitor'], 0],
        ['in', ['get', 'time_slot'], ['literal', VISITOR_AND_SPEND_TIME_SLOTS_NIGHT]],
      ],
      paint: {
        "fill-color": [
          "step",
          ["get", "visitor"],
          ...getVisitorAndSpendColorScale('visitor', 'night'),
        ],
        "fill-opacity": 0.7,
        "fill-outline-color": "#252C34",
      },
      layout: {
        visibility: "none",
      },
    },
    {
      id: "visitor-and-spend-visitor-hex-empty",
      type: "fill",
      filter: [
        // MapLibre doesn't like negative numbers
        'all',
        ['has', 'visitor'],
        ['==', ['get', 'visitor'], 0],
      ],
      paint: {
        "fill-color": 'grey',
        "fill-opacity": 0.15,
        "fill-outline-color": '#252C34',
      },
      layout: {
        visibility: "none",
      },
    },
    {
      id: "visitor-and-spend-spend-grid",
      type: "fill",
      filter: [
        // MapLibre doesn't like negative numbers
        'all',
        ['has', 'txn_amt'],
        ['>', ['get', 'txn_amt'], 0],
      ],
      paint: {
        "fill-color": [
          "step",
          ["get", "txn_amt"],
          ...getVisitorAndSpendColorScale('spend', 'dayAndNight')
        ],
        "fill-opacity": 0.7,
        "fill-outline-color": "#252C34",
      },
      layout: {
        visibility: "none",
      },
    },
    {
      id: "visitor-and-spend-spend-grid-empty",
      type: "fill",
      filter: [
        // MapLibre doesn't like negative numbers
        'all',
        ['has', 'txn_amt'],
        ['==', ['get', 'txn_amt'], 0],
      ],
      paint: {
        "fill-color": 'grey',
        "fill-opacity": 0.15,
        "fill-outline-color": '#252C34',
      },
      layout: {
        visibility: "none",
      },
    },
    {
      id: "visitor-and-spend-visitor-label",
      type: "symbol",
      minzoom: 13,
      maxzoom: 20,
      filter: [
        // MapLibre doesn't like negative numbers
        'all',
        ['has', 'visitor'],
        ['any',
          ['all',
            ['in', ['get', 'time_slot'], ['literal', VISITOR_AND_SPEND_TIME_SLOTS_DAY]],
            ['>', ['get', 'visitor'], 0],
          ],
          ['all',
            ['in', ['get', 'time_slot'], ['literal', VISITOR_AND_SPEND_TIME_SLOTS_NIGHT]],
            ['>', ['get', 'visitor'], 0],
          ],
        ]
      ],
      paint: {
        'text-color': [
          'case',
          ['<', ['get', 'visitor'], 7500], 'black',
          'white',
        ],
        'text-opacity': 1,
      },
      layout: {
        visibility: "none",
        'text-field': ['get', 'visitor'],
        'text-font': ["Source Sans Pro Regular"],
        'text-size': 18,
      },
    },
    {
      id: "visitor-and-spend-spend-label",
      type: "symbol",
      minzoom: 14,
      maxzoom: 20,
      filter: [
        // MapLibre doesn't like negative numbers
        'all',
        ['has', 'txn_amt'],
        ['>', ['get', 'txn_amt'], 0],
      ],
      paint: {
        'text-color': [
          'case',
          ['<', ['get', 'txn_amt'], 600], 'black',
          'white',
        ],
        'text-opacity': 1,
      },
      layout: {
        visibility: "none",
        'text-field': ['get', 'txn_amt'],
        'text-font': ["Source Sans Pro Regular"],
        'text-size': 16,
      },
    },
  ],
  source: {
    id: "visitor-and-spend",
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: [],
    },
  },
  labels: {
    parent: "Visitor and Spend",
  },
  filters: {
    filterByChildren: false,
  },
  filterBySelectedRegion: false,
  category: "hsds",
  requiresGis2Token: true,
  customFetch: true,
  noColourPicker: true,
  toggleColor: chartColours.darkBlue,
  popupType: "visitor-and-spend-grid-cell",
  popupLayers: [
    "visitor-and-spend-visitor-hex-day",
    "visitor-and-spend-visitor-hex-night",
    "visitor-and-spend-spend-grid",
    "visitor-and-spend-visitor-hex-empty",
    "visitor-and-spend-spend-grid-empty",
  ],
  opacityLimits: {
    "visitor-and-spend-visitor-hex-empty": [0, 0.15],
    "visitor-and-spend-spend-grid-empty": [0, 0.15],
  },
  info: {
    shortDescription: "BT visitor counts and Matercard retail spend index figures.",
    longDescriptionTitle: "Visitor counts and retail spending",
    longDescription: [
      (<><b>Visitor and spend information is only available to members of the High Streets Data Service (HSDS).</b> For members the login button is at the bottom right of the sidebar.</>),
      "Visitor and spend figures are a historical average representing typical values for the selected time band. The historical average is derived from 12 consecutive months of data collected within the last 18 months.",
      (<><b>Visitor Counts:</b> BT's Footfall data providing the number of non-residents and non-workers who spend at least 10 minutes in that hex in that time band. Full dataset and methodology are available here: <a href="https://data.london.gov.uk/dataset/footfall-bt-people-counts-hsds" target="_blank">Footfall - BT People Counts - HSDS - London Datastore</a>.</>),
      (<><b>Retail Spend:</b> Mastercard's Retail Location Index (MRLI) measures relative credit and debit card spend on in-store retail purchases (online transactions are not included). Values are an index figure, not a £ amount, denoting relative spend compared to a historical London-wide average. A value of 100, for instance, denotes that the amount of spend in that area was equivalent to the historic city-wide spend. Full dataset and methodology are available here: <a href="https://data.london.gov.uk/dataset/spend-mastercard-retail-index-3-hourly" target="_blank">Spend - Mastercard Retail Index - 3-hourly - London Datastore</a>.</>),       
    ]
  }
}

export default layerDefs